import { MessageDescriptor } from "react-intl";

import {
  ChatChangeDealStageAction,
  ChatManageRelationshipAction,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";

import dealLockMessages from "@app/util/deal/dealLockMessages";
import { getStagesFunnel } from "@app/util/deal/dealLockPickers";
import getDealLock, { LockableDeal } from "@app/util/deal/getDealLock";

// The stored funnel, from its stages' embedded copy, which the funnels reload keeps current
export const storedFunnelOf = (deal: LockableDeal, funnelStages: Record<Funnel["id"], Stage[]>) => [
  getStagesFunnel(funnelStages, deal.funnel?.id),
];

// The deal's looked-up read when the card has one (useProposalDealRefs): it is fresher than the
// stored stages and says where the deal is now, not where it was when the card was proposed.
export const resolveChangeDealStageLock = (
  action: ChatChangeDealStageAction,
  funnelStages: Record<Funnel["id"], Stage[]>,
  dealRefs: Record<Deal["id"], LockableDeal>
): MessageDescriptor | undefined => {
  const deal = dealRefs[action.entityId] ?? {
    funnel: getStagesFunnel(funnelStages, action.funnelId),
    stage: action.currentStage,
  };
  const { canMoveStage, reason } = getDealLock(
    deal,
    storedFunnelOf(deal, funnelStages),
    funnelStages
  );
  return !canMoveStage && reason ? dealLockMessages[reason] : undefined;
};

// Linking or unlinking a company's deal rewrites the deal's company, an edit a read-only stage
// refuses. Judged once the deal is looked up; until then the server is the backstop.
export const resolveRelationshipDealLock = (
  action: ChatManageRelationshipAction,
  draft: Record<string, unknown> | undefined,
  funnelStages: Record<Funnel["id"], Stage[]>,
  dealRefs: Record<Deal["id"], LockableDeal>
): MessageDescriptor | undefined => {
  if (action.relationshipKey !== "account.deals") {
    return undefined;
  }
  const dealId =
    typeof draft?.relatedEntityId === "number" ? draft.relatedEntityId : action.relatedEntity?.id;
  const deal = typeof dealId === "number" ? dealRefs[dealId] : undefined;
  if (!deal) {
    return undefined;
  }
  const { canEdit, reason } = getDealLock(deal, storedFunnelOf(deal, funnelStages), funnelStages);
  return !canEdit && reason ? dealLockMessages[reason] : undefined;
};
